import { useEffect, useContext } from "react";
import GlobalContext from "../context/store";

function formatPhoneNumber(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length !== 10) return "";
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function TermsSection({ title, children }) {
  return (
    <section>
      <h2>{title}</h2>
      <p>{children}</p>
    </section>
  );
}

export default function Terms() {
  const { company } = useContext(GlobalContext);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const COMPANY_NAME = company?.name || "Dollhouse Mermaid";
  const COMPANY_URL = company?.urls?.[0] || window.location.hostname;
  const formattedPhone = formatPhoneNumber(company?.phone);
  const COMPANY_PHONE = formattedPhone
    ? `+1 ${formattedPhone}`
    : "";
  const COMPANY_EMAIL = company?.email || "info@dollhousemermaid.com";
  const EFFECTIVE_DATE = "April 1, 2026";
  const LAST_REVISED = "May 11, 2026";

  return (
    <main className="ticket-page">
      <div className="container ticket-legal">
        <header className="ticket-legal__header">
          <p className="ticket-eyebrow">Legal</p>
          <h1 className="ticket-legal__title">Terms & Conditions</h1>
          <p className="ticket-legal__updated">Last updated: {LAST_REVISED}</p>
        </header>

        <article className="ticket-legal__content">
          <TermsSection title={`Terms & Conditions — ${COMPANY_URL}`}>
            Effective Date: {EFFECTIVE_DATE}
            <br />
            Last Revised: {LAST_REVISED}
            <br />
            <br />
            Welcome to {COMPANY_URL}. These Terms and Conditions (&quot;Terms&quot;)
            govern your access to and use of our website, services, ticketing
            system, and any related offerings (collectively, the
            &quot;Service&quot;). By using or purchasing through {COMPANY_URL},
            you agree to these Terms. If you do not agree, please do not use the
            Service.
          </TermsSection>

          <TermsSection title="1. About Us">
            {COMPANY_NAME}, Inc. (&quot;{COMPANY_NAME},&quot; &quot;we,&quot;
            &quot;our,&quot; or &quot;us&quot;) operates {COMPANY_URL}.
            <br />
            <br />
            Support: {COMPANY_EMAIL}
          </TermsSection>

          <TermsSection title="2. Scope of the Terms">
            These Terms apply to everyone who visits or uses {COMPANY_URL} or
            purchases tickets through the Service, including but not limited to
            ticket buyers, guests, event attendees, and website visitors.
            <br />
            <br />
            Additional rules may apply for specific events or venues including age
            restrictions, dress codes, entry times, and venue policies. These
            additional rules will be posted on the event page or communicated by
            the venue. In the event of a conflict, the event-specific policies
            will govern for that event.
          </TermsSection>

          <TermsSection title="3. Eligibility">
            You must be at least 18 years old or the legal age of majority in your
            jurisdiction to create an account or purchase tickets.
            <br />
            <br />
            Certain events may require guests to be 21 years of age or older with
            valid government-issued identification. By purchasing tickets, you
            represent that you meet all eligibility requirements.
          </TermsSection>

          <TermsSection title="4. Accounts & Security">
            If you create an account, you agree to:
            <br />
            • Provide accurate, current, and complete information.
            <br />
            • Maintain and promptly update your account information.
            <br />
            • Keep your login credentials secure.
            <br />
            • Notify us immediately if you believe your account has been compromised.
            <br />
            <br />
            You are responsible for all activity conducted through your account.
          </TermsSection>

          <TermsSection title="5. Ticket Purchases">
            <strong>a. Sale Process</strong>
            <br />
            <br />
            Purchasing a ticket creates a transaction between you and the event
            organizer or venue hosting the event. Tickets are considered valid only
            after successful payment authorization and confirmation.
            <br />
            <br />
            <strong>b. Prices &amp; Taxes</strong>
            <br />
            <br />
            Ticket prices may include service fees, venue fees, or other charges
            determined by the event organizer. Taxes may apply depending on local
            regulations.
            <br />
            <br />
            <strong>c. Payment &amp; Checkout Agreement</strong>
            <br />
            <br />
            Payments are processed through secure third-party payment processors.
            By completing a purchase, you authorize the applicable charges to your
            selected payment method.
            <br />
            <br />
            <strong>IMPORTANT:</strong> Before completing your purchase, you must
            actively toggle the Terms &amp; Conditions acceptance at checkout. By
            toggling this acceptance and clicking &quot;Checkout,&quot; you
            explicitly confirm that you have read, understood, and agree to these
            Terms in their entirety — including the No Refunds Policy in Section 6.
            No purchase can be completed without this acceptance. This action
            constitutes your binding agreement to all terms herein.
            <br />
            <br />
            <strong>d. Delivery</strong>
            <br />
            <br />
            Tickets are delivered electronically through email, mobile wallet, or
            digital ticketing. It is your responsibility to ensure that your
            contact information is accurate at the time of purchase. Electronic
            delivery of your ticket confirmation constitutes completed delivery of
            the purchased product. {COMPANY_NAME} bears no responsibility for
            delivery failures caused by inaccurate contact information provided at
            checkout.
            <br />
            <br />
            <strong>e. Proof of Delivery &amp; Scan Records</strong>
            <br />
            <br />
            All tickets are issued digitally and tracked through our ticketing
            infrastructure. The following each constitute conclusive proof that the
            ticket was delivered and the service was rendered:
            <br />
            <br />
            • Electronic delivery of the ticket confirmation to the email address
            provided at checkout.
            <br />
            • Entry scan records showing the ticket was scanned at the venue.
            <br />
            • Access logs confirming the ticket link or mobile wallet pass was
            accessed.
            <br />
            <br />
            In the event of a payment dispute or chargeback, {COMPANY_NAME} will
            present these records as evidence that the product was delivered and/or
            the service was made available to the purchaser.
          </TermsSection>

          <TermsSection title="6. Refunds, Exchanges & Cancellations">
            ALL SALES ARE FINAL AND NON-REFUNDABLE. By completing your purchase and
            accepting these Terms at checkout, you acknowledge and agree that you
            are not entitled to a refund under any circumstance except as expressly
            stated in Section 6(b) below.
            <br />
            <br />
            <strong>a. No Refund Policy</strong>
            <br />
            <br />
            All ticket sales are final. No refunds, exchanges, credits, or
            transfers will be issued for any reason, including but not limited to:
            <br />
            • Change of plans or personal circumstances.
            <br />
            • Failure to attend the event for any reason, including illness, travel
            disruption, or scheduling conflicts.
            <br />
            • Dissatisfaction with the event, performers, or venue.
            <br />
            • Failure to meet entry requirements (see Section 8). If you are denied
            entry due to failure to present valid ID, non-compliance with the dress
            code, intoxication, age restrictions, or any other venue policy, no
            refund will be issued. All sales remain final.
            <br />
            • Event postponement or rescheduling to a future date.
            <br />
            • Acts of God, force majeure, or circumstances beyond our control.
            <br />
            <br />
            <strong>b. Event Cancellation by {COMPANY_NAME}</strong>
            <br />
            <br />
            If an event is cancelled by {COMPANY_NAME} and is not rescheduled
            within 90 days of the original event date, we may, at our sole
            discretion, issue a credit toward a future event of equal or lesser
            value. Refunds to the original payment method are not guaranteed and will
            only be considered in exceptional circumstances at our sole discretion.
            <br />
            <br />
            For clarity: postponement, venue change, lineup change, or any
            modification to the event that does not constitute full cancellation does
            not entitle any purchaser to a refund or credit.
            <br />
            <br />
            <strong>c. No Cancellation Right</strong>
            <br />
            <br />
            You may not cancel your ticket purchase after the transaction is
            completed. The right of withdrawal or cancellation does not apply to
            event ticket purchases under applicable consumer protection laws, as
            tickets constitute time-specific services.
          </TermsSection>

          <TermsSection title="7. Transfer & Resale">
            Tickets may only be transferred or resold if the event organizer
            expressly permits it.
            <br />
            <br />
            Unauthorized resale or reproduction of tickets may result in
            cancellation without refund.
            <br />
            <br />
            Tickets purchased through unofficial resale channels may be invalid and
            may not be honored at the venue.
          </TermsSection>

          <TermsSection title="8. Event Conduct & Venue Rules">
            All guests must follow venue rules, event policies, and instructions
            provided by event staff.
            <br />
            <br />
            Entry requirements may include:
            <br />
            • Valid government-issued photo ID
            <br />
            • Compliance with dress codes
            <br />
            • Arrival before specified entry times
            <br />
            • Adherence to age restrictions
            <br />
            <br />
            Event organizers and venues reserve the right to refuse entry or remove
            guests for violations of venue policy, disruptive behavior,
            intoxication, or safety concerns. In all such cases, no refund will be
            issued and all sales remain final.
          </TermsSection>

          <TermsSection title="9. Chargebacks & Payment Disputes">
            Filing a chargeback, payment dispute, or similar claim with your bank or
            card issuer for a valid, non-refundable purchase that was delivered or
            made available as described in these Terms constitutes a material breach
            of this agreement.
            <br />
            <br />
            In the event of a chargeback or dispute, {COMPANY_NAME} reserves the
            right to:
            <br />
            • Submit all available evidence to the payment processor, including
            purchase records, ticket delivery confirmation, entry scan logs, and
            these Terms as accepted at checkout.
            <br />
            • Pursue recovery of the disputed amount through available legal and
            collection channels.
            <br />
            • Permanently ban the purchaser from future ticket purchases on{" "}
            {COMPANY_URL} and affiliated platforms.
            <br />
            <br />
            By accepting these Terms at checkout, you acknowledge that filing a
            fraudulent or bad-faith chargeback for a delivered service may constitute
            fraud and may be reported to relevant authorities.
          </TermsSection>

          <TermsSection title="10. Intellectual Property">
            All content on {COMPANY_URL} including text, graphics, logos, images,
            software, and designs is owned or licensed by {COMPANY_NAME} and is
            protected by intellectual property laws.
            <br />
            <br />
            You may not reproduce, distribute, modify, or create derivative works
            from the content without written permission.
          </TermsSection>

          <TermsSection title="11. User Content">
            Users may submit reviews, comments, or other materials where permitted.
            <br />
            <br />
            By submitting User Content, you grant {COMPANY_NAME} a non-exclusive,
            worldwide, royalty-free license to display, distribute, and use the
            content in connection with the Service.
          </TermsSection>

          <TermsSection title="12. Prohibited Conduct">
            Users may not:
            <br />
            • Violate any applicable law or third-party rights
            <br />
            • Attempt to access accounts they do not own
            <br />
            • Use the Service to scam or defraud others
            <br />
            • Interfere with the operation or security of the platform
            <br />
            • Circumvent ticketing restrictions imposed by organizers or venues
            <br />
            <br />
            Violations may result in account suspension or ticket cancellation without
            refund.
          </TermsSection>

          <TermsSection title="13. Live On Beat Platform">
            Ticketing services used on this website are powered by Live On Beat, a
            ticketing infrastructure provider.
            <br />
            <br />
            Live On Beat provides software and payment processing infrastructure only
            and does not organize, host, manage, or operate events.
            <br />
            <br />
            All events listed on this website are organized by the event organizer
            or venue.
            <br />
            <br />
            Live On Beat is not responsible for:
            <br />
            • Event scheduling or cancellation
            <br />
            • Event operations
            <br />
            • Entry policies or venue rules
            <br />
            • Refund decisions
            <br />
            • Event safety or guest experience
            <br />
            <br />
            All event-related inquiries must be directed to the event organizer or
            venue.
          </TermsSection>

          <TermsSection title="14. Communications and SMS Marketing">
            By creating an account or purchasing tickets through the Service, you
            agree that {COMPANY_NAME} may contact you via email regarding your
            purchase, event updates, or important service announcements.
            <br />
            <br />
            If you voluntarily provide your phone number and opt in to SMS
            communications during checkout or account registration, you consent to
            receive marketing messages, event announcements, and promotions from{" "}
            {COMPANY_NAME}. Message frequency may vary. Message and data rates may
            apply. You may opt out at any time by replying STOP to any message. Your
            consent to receive marketing messages is not a condition of purchase.
          </TermsSection>

          <TermsSection title="15. Disclaimer of Warranties">
            The Service is provided &quot;as is&quot; and &quot;as available&quot;
            without warranties of any kind.
            <br />
            <br />
            {COMPANY_NAME} makes no guarantees regarding uninterrupted access,
            security, or error-free operation of the platform.
          </TermsSection>

          <TermsSection title="16. Limitation of Liability">
            To the maximum extent permitted by law, {COMPANY_NAME} and its affiliates
            shall not be liable for indirect, incidental, or consequential damages
            arising from:
            <br />
            • Use of the Service
            <br />
            • Ticket purchases
            <br />
            • Event attendance
            <br />
            • Event cancellations or schedule changes
            <br />
            <br />
            Total liability shall not exceed the amount paid for the ticket.
            <br />
            <br />
            {COMPANY_NAME} is not responsible for any incidental costs incurred by
            ticket holders in connection with attending or attempting to attend an
            event, including but not limited to travel, lodging, transportation,
            meals, or other out-of-pocket expenses, regardless of the reason for
            event cancellation, postponement, or modification.
          </TermsSection>

          <TermsSection title="17. Indemnification">
            You agree to indemnify and hold harmless {COMPANY_NAME} and its
            affiliates from claims arising from your use of the Service or your
            conduct at events.
          </TermsSection>

          <TermsSection title="18. Privacy & Cookies">
            We collect and process personal information according to our Privacy
            Policy, which explains how we collect, use, and protect your information.
          </TermsSection>

          <TermsSection title="19. Changes to These Terms">
            We may update these Terms periodically. Continued use of the Service after
            changes constitutes acceptance of the updated Terms. It is your
            responsibility to review these Terms before each purchase.
          </TermsSection>

          <TermsSection title="20. Governing Law">
            These Terms are governed by the laws of the State of California, without
            regard to its conflict of law provisions.
          </TermsSection>

          <TermsSection title="21. Dispute Resolution">
            In the event of a dispute between you and {COMPANY_NAME}, both parties
            agree to first attempt resolution through good-faith negotiation. If
            negotiation fails, an independent third-party arbiter may be consulted
            to resolve the issue. The arbiter&apos;s decision will be final and
            binding.
          </TermsSection>

          <TermsSection title="22. Severability">
            If any provision is found invalid or unenforceable, the remaining
            provisions remain in effect.
          </TermsSection>

          <TermsSection title="23. Contact">
            {COMPANY_NAME}
            <br />
            {COMPANY_EMAIL}
            <br />
            {COMPANY_PHONE}
          </TermsSection>

          <TermsSection title="Acknowledgment of Terms">
            By toggling the Terms &amp; Conditions acceptance at checkout and
            completing your purchase on {COMPANY_URL}, you acknowledge that you have
            read, understood, and agree to be legally bound by these Terms &amp;
            Conditions in their entirety, including the No Refunds Policy. This
            agreement is entered into at the time of purchase and is binding on you
            and your successors.
          </TermsSection>
        </article>
      </div>
    </main>
  );
}
